import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertDetailsModal = ({ alert, onClose, onAcknowledge, onResolve }) => {
  const [notes, setNotes] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  if (!alert) return null;

  const handleResolve = async () => {
    setIsResolving(true);
    await onResolve(alert?.id, notes);
    setIsResolving(false);
  };

  const severityColor = alert?.alertSeverity === 'critical' ? 'text-error' : 'text-warning';
  const severityBg = alert?.alertSeverity === 'critical' ? 'bg-error/10' : 'bg-warning/10';
  const severityBorder = alert?.alertSeverity === 'critical' ? 'border-error' : 'border-warning';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div 
        className="bg-card rounded-2xl shadow-therapeutic max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e?.stopPropagation()}
      >
        {/* Header */}
        <div className={`${severityBg} border-b-2 ${severityBorder} p-6`}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full ${alert?.alertSeverity === 'critical' ? 'bg-error' : 'bg-warning'} flex items-center justify-center`}>
                <Icon 
                  name={alert?.alertSeverity === 'critical' ? 'AlertTriangle' : 'AlertCircle'} 
                  size={24} 
                  className="text-white" 
                  strokeWidth={2.5} 
                />
              </div>
              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-md ${alert?.alertSeverity === 'critical' ? 'bg-error' : 'bg-warning'} text-white text-sm font-bold mb-2`}>
                  {alert?.alertSeverity === 'critical' ? 'CRITICAL ALERT' : 'HIGH SEVERITY ALERT'}
                </span>
                <h2 className="text-xl font-heading font-bold text-foreground">
                  {alert?.dependentName}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-lg p-2"
              aria-label="Close modal"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Time Information */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} />
            <span>
              Logged {new Date(alert?.symptomDetails?.timestamp)?.toLocaleString('en-US', {
                dateStyle: 'medium',
                timeStyle: 'short'
              })}
            </span>
          </div>

          {/* Symptom Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-heading font-bold text-foreground">Symptom Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Severity Level</p>
                <p className={`text-lg font-bold ${severityColor}`}>
                  {alert?.symptomDetails?.severity || 'Not specified'}
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-xs text-muted-foreground mb-1">Feeling Level</p>
                <p className="text-lg font-bold text-foreground">
                  {alert?.symptomDetails?.feelingLevel || 'Not specified'}
                </p>
              </div>
            </div>

            {alert?.symptomDetails?.symptomCategories && alert?.symptomDetails?.symptomCategories?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Symptoms</p>
                <div className="flex flex-wrap gap-2">
                  {alert?.symptomDetails?.symptomCategories?.map((symptom, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                    >
                      {symptom}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {alert?.symptomDetails?.dailyImpacts && alert?.symptomDetails?.dailyImpacts?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Daily Life Impacts</p>
                <div className="flex flex-wrap gap-2">
                  {alert?.symptomDetails?.dailyImpacts?.map((impact, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-warning/10 text-warning text-sm font-medium"
                    >
                      {impact}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {alert?.symptomDetails?.notes && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Additional Notes</p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-foreground">{alert?.symptomDetails?.notes}</p>
                </div>
              </div>
            )}
          </div>

          {/* Recommended Actions */}
          <div className="bg-info/10 border-l-4 border-info rounded-lg p-4">
            <h4 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
              <Icon name="Info" size={16} className="text-info" />
              Recommended Actions
            </h4>
            <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
              <li>Contact {alert?.dependentName} to check on their condition</li>
              <li>Consider scheduling a medical consultation if symptoms persist</li>
              <li>Monitor for any changes or worsening symptoms</li>
              <li>Document any actions taken in the resolution notes below</li>
            </ul>
          </div>

          {/* Resolution Notes */}
          {alert?.alertStatus !== 'resolved' && (
            <div>
              <label htmlFor="resolution-notes" className="block text-sm font-medium text-foreground mb-2">
                Resolution Notes (Optional)
              </label>
              <textarea
                id="resolution-notes"
                value={notes}
                onChange={(e) => setNotes(e?.target?.value)}
                placeholder="Add notes about actions taken or follow-up needed..."
                className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                rows={4}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {alert?.alertStatus === 'pending' || alert?.alertStatus === 'sent' ? (
              <>
                <Button
                  onClick={() => onAcknowledge(alert?.id)}
                  variant="outline"
                  className="flex-1"
                >
                  <Icon name="Check" size={18} className="mr-2" />
                  Acknowledge
                </Button>
                <Button
                  onClick={handleResolve}
                  disabled={isResolving}
                  className="flex-1"
                >
                  <Icon name="CheckCircle" size={18} className="mr-2" />
                  {isResolving ? 'Resolving...' : 'Mark as Resolved'}
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2 text-success">
                <Icon name="CheckCircle" size={20} />
                <span className="text-sm font-medium">
                  {alert?.alertStatus === 'acknowledged' ? 'Acknowledged' : 'Resolved'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertDetailsModal;